import { useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import Stack from '@mui/material/Stack';

import { useAuthContext } from 'src/auth/hooks';
import { useOrganizationContext } from 'src/context/organization';
import { GET_ANNOUNCEMENTS } from 'src/graphql/announcements/queries';
import { useLocalStorage } from 'src/hooks/use-local-storage';
import { useEffectiveRole } from 'src/hooks/use-effective-role';
import { useOrganizationStates } from 'src/hooks/useOrganizationStates';
import { usePathname } from 'src/routes/hook';
import { hasuraRoleOf } from 'src/utils/roles';

import AnnouncementAlert from './announcement-alert';

/**
 * Freshness rides on USER ACTIVITY, not a timer: the query re-runs on a route change or on the
 * tab becoming visible again, throttled to once per this interval. No background polling — an
 * idle tab sends nothing — and deliberately no Hasura subscription either (owner call,
 * 2026-08-18): Hasura serves subscriptions by polling Postgres itself at 1s intervals and holds
 * a websocket per client for the whole session — strictly more load than activity-driven
 * refetches for a table that changes a few times a month — and nothing in this app has ever
 * exercised the WS link in production.
 */
const REFRESH_THROTTLE_MS = 2 * 60 * 1000;

/**
 * The announcement strip at the top of every authenticated page, driven entirely by
 * ri_apps_prd.announcements — rows are managed from the superadmin screen at
 * /superadmin/announcements, whose form is the authority on the row vocabulary. Hasura's select
 * permission serves only active rows; the schedule window, role and state targeting and dismissal
 * are evaluated here, where the clock, the effective role and this browser's dismissal history live.
 *
 * A state-targeted row shows when the selected org has a voting area in one of its states. With no
 * org selected (superadmins, national viewers) there are no states, so only untargeted rows show.
 *
 * Every eligible row shows, stacked: non-dismissible rows (maintenance) first, then newest first.
 * Dismissals are per BROWSER — localStorage keyed by announcement_id — so a new row always shows.
 *
 * Renders nothing on query error, deliberately: the app deploys ahead of the table being tracked
 * in Hasura, and a banner must never be able to break every page it sits on.
 */
export default function AnnouncementBanner() {
  // notifyOnNetworkStatusChange: a refetch returning UNCHANGED rows would otherwise not
  // re-render, and the window predicate below reads the clock at render time — a scheduled
  // banner whose starts_at passed since the last render would stay hidden until something else
  // re-rendered the layout.
  //
  // Skipped for Hasura role `user` (a volunteer who manages no org): it has no select permission
  // on announcements, so asking only logs a validation error on every page, and the banner
  // renders nothing on error anyway. Announcements targeted at Volunteers therefore reach only
  // volunteers who also manage an org, whose Hasura role is `manager`.
  const { user } = useAuthContext();
  const skip = hasuraRoleOf(user) === 'user';
  const { data, refetch } = useQuery(GET_ANNOUNCEMENTS, {
    notifyOnNetworkStatusChange: true,
    skip,
  });
  const pathname = usePathname();

  // Mount counts as a fetch (useQuery just ran) — the ref keeps the mount-triggered pathname
  // effect from immediately fetching a second time.
  const lastFetchedRef = useRef(Date.now());
  const refetchIfStale = () => {
    // refetch() sends the query even when `skip` is set, so the skip has to be honored here too.
    if (skip) return;
    if (Date.now() - lastFetchedRef.current < REFRESH_THROTTLE_MS) return;
    lastFetchedRef.current = Date.now();
    refetch();
  };

  // Route change: the "at worst they see it on the next page" guarantee.
  useEffect(() => {
    refetchIfStale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Tab becoming visible again: someone returning to a long-idle tab catches up immediately
  // instead of waiting for their next navigation.
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) refetchIfStale();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const role = useEffectiveRole();
  const { selectedOrganizationId } = useOrganizationContext();
  // Only asked for while some active row is state-targeted, so untargeted banners cost no query.
  const { states: orgStates } = useOrganizationStates(selectedOrganizationId, {
    skip: !(data?.rows ?? []).some((row) => row.states),
  });
  const [dismissed, setDismissed] = useLocalStorage('dismissed-announcements', []);

  const now = new Date();
  const visible = (data?.rows ?? []).filter(
    (row) =>
      (row.starts_at == null || new Date(row.starts_at) <= now) &&
      (row.ends_at == null || now <= new Date(row.ends_at)) &&
      (row.roles == null || row.roles.includes(role)) &&
      (row.states == null || row.states.some((state) => orgStates.includes(state))) &&
      (!row.dismissible || !(dismissed ?? []).includes(row.announcement_id))
  );

  if (!visible.length) return null;

  const banners = visible.sort((a, b) =>
    a.dismissible === b.dismissible
      ? b.announcement_id - a.announcement_id
      : a.dismissible - b.dismissible
  );

  const dismiss = (id) => setDismissed((prev) => [...(prev ?? []), id]);

  return (
    // Own side margins: Main has no horizontal padding on mobile (none at all in the horizontal
    // layout) — pages bring their own Container, this strip does not get one.
    <Stack spacing={1} sx={{ mx: 2, mb: 2 }}>
      {banners.map((banner) => (
        <AnnouncementAlert
          key={banner.announcement_id}
          announcement={banner}
          onClose={() => dismiss(banner.announcement_id)}
        />
      ))}
    </Stack>
  );
}
